import React, { useEffect, useState } from 'react';
import { useSimulationStore } from '../../store/useSimulationStore';
import { useCanvasStore } from '../../store/useCanvasStore';
import { ModeSelector } from '../controls/ModeSelector';
import { WebVitalsState } from '../../types/vitals';
import { formatMetricValue, getOverallRating } from '../../services/vitals';
import { formatCurrencyUAH, formatSimulatedMonthDate } from '../../utils/formatters';
import { AuthUser } from '../../services/auth';
import { saveStructureToCloud } from '../../services/structureLibrary';
import {
  Maximize2,
  Minimize2,
  FolderKanban,
  Zap,
  BookmarkCheck,
  Save,
  Check,
  Printer,
  ShieldCheck,
  User,
  LogOut,
  HelpCircle,
} from 'lucide-react';

export interface AppHeaderProps {
  vitalsState: WebVitalsState;
  onOpenWebVitals: () => void;
  onOpenPresets?: () => void;
  onOpenLibrary?: () => void;
  onOpenDocumentExport?: () => void;
  onLockSession?: () => void;
  currentUser?: AuthUser | null;
  onOpenAdminAccounts?: () => void;
  onStartTour?: () => void;
}

export const AppHeader: React.FC<AppHeaderProps> = ({
  vitalsState,
  onOpenWebVitals,
  onOpenPresets,
  onOpenLibrary,
  onOpenDocumentExport,
  onLockSession,
  currentUser,
  onOpenAdminAccounts,
  onStartTour,
}) => {
  const mode = useSimulationStore((state) => state.mode);
  const setMode = useSimulationStore((state) => state.setMode);
  const selectedMonth = useSimulationStore((state) => state.selectedMonth);
  const simulationStartDate = useSimulationStore((state) => state.simulationStartDate);
  const getCurrentPayoutBreakdown = useSimulationStore((state) => state.getCurrentPayoutBreakdown);
  const getCurrentRootNode = useSimulationStore((state) => state.getCurrentRootNode);
  const activeStructureTitle = useSimulationStore((state) => state.activeStructureTitle);
  const setIsFullscreen = useCanvasStore((state) => state.setIsFullscreen);

  const [isFullscreenActive, setIsFullscreenActive] = useState(false);
  const [isQuickSaving, setIsQuickSaving] = useState(false);
  const [quickSaveSuccess, setQuickSaveSuccess] = useState(false);

  const handleQuickSave = async () => {
    if (isQuickSaving) return;
    setIsQuickSaving(true);
    try {
      const root = getCurrentRootNode();
      const title = activeStructureTitle || `${root.name} (Р${root.level})`;
      await saveStructureToCloud(root, title);
      setQuickSaveSuccess(true);
      setTimeout(() => setQuickSaveSuccess(false), 2000);
    } catch (e) {
      console.warn('Quick save error:', e);
    } finally {
      setIsQuickSaving(false);
    }
  };

  const payout = getCurrentPayoutBreakdown();
  const overallRating = getOverallRating(vitalsState);

  // Fullscreen handling
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => {
        setIsFullscreen(true);
        setIsFullscreenActive(true);
      }).catch((err) => {
        console.warn('Fullscreen error:', err);
      });
    } else {
      document.exitFullscreen().then(() => {
        setIsFullscreen(false);
        setIsFullscreenActive(false);
      }).catch((err) => {
        console.warn('Exit fullscreen error:', err);
      });
    }
  };

  useEffect(() => {
    const handleFsChange = () => {
      const isFs = !!document.fullscreenElement;
      setIsFullscreenActive(isFs);
      setIsFullscreen(isFs);
    };
    document.addEventListener('fullscreenchange', handleFsChange);
    return () => document.removeEventListener('fullscreenchange', handleFsChange);
  }, [setIsFullscreen]);

  const lcpValue = vitalsState.lcp ? formatMetricValue('LCP', vitalsState.lcp.value) : '0.4s';
  const clsValue = vitalsState.cls ? formatMetricValue('CLS', vitalsState.cls.value) : '0.00';

  const isAdmin = currentUser?.role === 'admin';

  return (
    <header className="sticky top-0 z-40 w-full bg-[#0A0F1D]/90 backdrop-blur-xl border-b border-white/10 px-3 sm:px-6 py-2 overflow-x-clip">
      <div className="max-w-[1920px] mx-auto flex items-center justify-between gap-2 overflow-x-clip">
        {/* Brand & Logo */}
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-tr from-accent-emerald to-accent-cyan flex items-center justify-center text-slate-950 font-extrabold text-base shadow-[0_0_15px_rgba(16,185,129,0.4)] flex-shrink-0">
            SL
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-sm sm:text-base font-bold tracking-tight text-white flex items-center gap-1.5">
                Starlife Simulator
              </h1>
              <span className="hidden sm:inline-block text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-accent-neon border border-emerald-500/25 font-medium">
                v2.0 Pro
              </span>
            </div>
          </div>
        </div>

        {/* Center: Mode Selector (Desktop / Tablet) */}
        {/* Center: Mode Selector (Desktop >= 1024px) */}
        <div className="hidden lg:flex items-center justify-center">
          <ModeSelector mode={mode} onChange={setMode} />
        </div>

        {/* Right Section: Controls, User profile, Admin button, Fullscreen */}
        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
          {/* Quick Header Income Pill (Tablet / Desktop) */}
          <div className="hidden md:flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-xl bg-surface-hover/80 border border-white/5 text-xs font-mono whitespace-nowrap shrink-0 flex-nowrap">
            <span className="text-text-muted whitespace-nowrap">Чек:</span>
            <span className="font-bold text-accent-neon whitespace-nowrap">
              {formatCurrencyUAH(payout.totalGrossIncomeUAH, 0)}
            </span>
            {mode === 'pathway' && (
              <span className="text-[11px] text-text-muted whitespace-nowrap hidden 2xl:inline">({selectedMonth} міс • {formatSimulatedMonthDate(selectedMonth, simulationStartDate)})</span>
            )}
          </div>

          {/* Admin Management Button (Visible only to Admin) */}
          {isAdmin && onOpenAdminAccounts && (
            <button
              type="button"
              onClick={onOpenAdminAccounts}
              className="
                flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-xl text-xs font-semibold
                bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 hover:from-emerald-500/30 hover:to-cyan-500/30
                border border-accent-emerald/40 hover:border-accent-neon text-accent-neon
                shadow-[0_0_12px_rgba(0,229,153,0.15)] transition-all cursor-pointer min-h-[38px] shrink-0
              "
              title="Панель керування консультантами та доступами"
            >
              <ShieldCheck className="w-4 h-4 text-accent-neon" />
              <span className="hidden 2xl:inline">Керування доступами</span>
            </button>
          )}

          {/* Document Export Button (Icon-only w-9 h-9) */}
          {onOpenDocumentExport && (
            <button
              data-tour="document-export"
              type="button"
              onClick={onOpenDocumentExport}
              className="
                w-9 h-9 min-w-[36px] min-h-[36px] shrink-0 aspect-square rounded-xl
                bg-surface-hover hover:bg-slate-700/80 border border-white/10 hover:border-accent-emerald/40
                text-text-primary flex items-center justify-center transition-all cursor-pointer
              "
              title="Сформувати комплексний документ (мапа, цілі, чек)"
              aria-label="Сформувати комплексний документ"
            >
              <Printer className="w-4 h-4 text-accent-emerald" />
            </button>
          )}

          {/* Quick 1-Click Save Button (Cloudflare KV) */}
          <button
            data-tour="quick-save-btn"
            type="button"
            onClick={handleQuickSave}
            disabled={isQuickSaving}
            className={`
              flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-xl text-xs font-medium
              transition-all cursor-pointer min-h-[38px] shrink-0 border
              ${
                quickSaveSuccess
                  ? 'bg-emerald-500/20 text-accent-neon border-emerald-500/40 shadow-[0_0_10px_rgba(0,229,153,0.3)]'
                  : 'bg-surface-hover hover:bg-slate-700/80 border-white/10 hover:border-accent-emerald/40 text-text-primary'
              }
            `}
            title="Швидке збереження версії поточної структури в хмару (1-клік)"
          >
            {quickSaveSuccess ? (
              <>
                <Check className="w-4 h-4 text-accent-neon" />
                <span className="hidden 2xl:inline text-accent-neon font-semibold">Збережено!</span>
              </>
            ) : (
              <>
                <Save className={`w-4 h-4 text-accent-neon ${isQuickSaving ? 'animate-spin' : ''}`} />
                <span className="hidden 2xl:inline">Зберегти</span>
              </>
            )}
          </button>

          {/* Structure Library Button */}
          {onOpenLibrary && (
            <button
              data-tour="structure-library-btn"
              type="button"
              onClick={onOpenLibrary}
              className="
                flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-xl text-xs font-medium
                bg-surface-hover hover:bg-slate-700/80 border border-white/10 hover:border-amber-400/40
                text-text-primary transition-all cursor-pointer min-h-[38px] shrink-0
              "
              title="Бібліотека збережених структур (Хмара Cloudflare)"
            >
              <BookmarkCheck className="w-4 h-4 text-amber-400" />
              <span className="hidden 2xl:inline">Бібліотека</span>
            </button>
          )}

          {/* Sandbox Preset Selector Button */}
          {mode === 'sandbox' && onOpenPresets && (
            <button
              type="button"
              onClick={onOpenPresets}
              className="
                flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium
                bg-surface-hover hover:bg-slate-700/80 border border-white/10 hover:border-cyan-400/40
                text-text-primary transition-all cursor-pointer min-h-[38px] shrink-0
              "
              title="Готові шаблони команд"
            >
              <FolderKanban className="w-4 h-4 text-cyan-400" />
              <span className="hidden 2xl:inline">Шаблони</span>
            </button>
          )}

          {/* Web-Vitals Performance Status Pill - Only visible to Admin */}
          {isAdmin && (
            <button
              type="button"
              onClick={onOpenWebVitals}
              className={`
                flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-xl text-xs font-mono font-medium
                border transition-all cursor-pointer min-h-[38px] select-none shrink-0
                ${
                  overallRating === 'good'
                    ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30 hover:border-emerald-400 shadow-[0_0_10px_rgba(16,185,129,0.15)]'
                    : 'bg-amber-500/10 text-amber-300 border-amber-500/30 hover:border-amber-400'
                }
              `}
              title="Переглянути телеметрію Google Core Web Vitals"
            >
              <Zap className="w-3.5 h-3.5 text-accent-neon fill-current" />
              <span className="hidden xl:inline">LCP: {lcpValue}</span>
              <span className="hidden 2xl:inline">| CLS: {clsValue}</span>
              <span className="inline xl:hidden">CWV</span>
            </button>
          )}

          {/* Interactive Walkthrough / Tour Button */}
          {onStartTour && (
            <button
              type="button"
              onClick={onStartTour}
              className="
                w-9 h-9 sm:w-9 sm:h-9 min-w-[36px] min-h-[36px] shrink-0 aspect-square rounded-xl bg-surface-hover hover:bg-slate-700
                border border-white/10 hover:border-accent-neon/40 flex items-center justify-center text-text-secondary
                hover:text-accent-neon transition-all cursor-pointer
              "
              title="Інтерактивний тур по симулятору"
              aria-label="Інтерактивний тур"
            >
              <HelpCircle className="w-4 h-4 text-cyan-400" />
            </button>
          )}

          {/* Fullscreen Button */}
          <button
            type="button"
            onClick={toggleFullscreen}
            className="
              w-9 h-9 sm:w-9 sm:h-9 min-w-[36px] min-h-[36px] shrink-0 aspect-square rounded-xl bg-surface-hover hover:bg-slate-700
              border border-white/10 flex items-center justify-center text-text-secondary
              hover:text-text-primary transition-all cursor-pointer
            "
            title={isFullscreenActive ? 'Вийти з повноекранного режиму' : 'Повноекранний режим'}
            aria-label="Повноекранний режим"
          >
            {isFullscreenActive ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>

          {/* User Profile Pill & Role Badge (Compact & Responsive on Tablet/Desktop) */}
          {currentUser && (
            <div className="hidden xl:flex items-center gap-2 pl-1.5 pr-2.5 py-1 rounded-xl bg-slate-900/80 border border-white/10 text-xs shrink-0">
              <div
                className={`
                  w-6 h-6 rounded-lg flex items-center justify-center shrink-0
                  ${
                    isAdmin
                      ? 'bg-emerald-500/20 text-accent-neon'
                      : 'bg-cyan-500/20 text-cyan-400'
                  }
                `}
              >
                {isAdmin ? <ShieldCheck className="w-3.5 h-3.5" /> : <User className="w-3.5 h-3.5" />}
              </div>
              <div className="flex items-center gap-1.5 text-left">
                <span
                  className="font-semibold text-white leading-tight max-w-[70px] sm:max-w-[100px] lg:max-w-[130px] truncate"
                  title={currentUser.name}
                >
                  {currentUser.name.replace(/\s*\([^)]*\)/g, '').trim() || currentUser.name}
                </span>
                <span
                  className={`
                    text-[10px] px-1.5 py-0.5 rounded font-medium shrink-0 leading-none
                    ${
                      isAdmin
                        ? 'bg-emerald-500/15 text-accent-neon border border-emerald-500/30'
                        : 'bg-cyan-500/15 text-cyan-400 border border-cyan-500/30'
                    }
                  `}
                >
                  {isAdmin ? 'Адмін' : 'Консультант'}
                </span>
              </div>
            </div>
          )}

          {/* Logout / Exit Session Button */}
          {onLockSession && (
            <button
              type="button"
              onClick={onLockSession}
              className="
                w-9 h-9 sm:w-9 sm:h-9 min-w-[36px] min-h-[36px] shrink-0 aspect-square rounded-xl bg-surface-hover hover:bg-red-500/20
                border border-white/10 hover:border-red-500/40 flex items-center justify-center text-text-secondary
                hover:text-red-400 transition-all cursor-pointer
              "
              title="Вийти з акаунта"
              aria-label="Вийти з акаунта"
            >
              <LogOut className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Mobile & Tablet Sub-Header: Mode Selector on screens < 1024px */}
      <div className="flex lg:hidden justify-center mt-2 pt-2 border-t border-white/5">
        <ModeSelector mode={mode} onChange={setMode} />
      </div>
    </header>
  );
};
